import { computed, observable } from 'mobx';

import { makePaginated, Paginated } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { TableQuery } from '../../adl-gen/common/tabular';
import { Competency, Skill } from '../../adl-gen/totalinteraction/futurefit/db';
import {
  fromPromise,
  // fromPromise,
  MaybeLoading
} from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';
import { AdminApplicationRoleType, ListEntryWithIdentity } from '../ui/common/list/list-types';

import { DataStore, TransportLayer } from './index';

class CompetencyApi implements TransportLayer<Competency> {
  kind: string = 'Competency';
  constructor(protected readonly service: Service) { }
  create = async (item: Competency): Promise<DbKey<Competency>> => this.service.createCompetency(item);
  update = async (itemWithId: WithDbId<Competency>): Promise<void> => this.service.updateCompetency(itemWithId);
  doDelete = async (id: DbKey<Competency>): Promise<void> => this.service.deleteCompetency(id);
  query = async (q: TableQuery): Promise<Paginated<WithDbId<Competency>>> => this.service.queryCompetencies(q);

  querySkills = async (q: TableQuery): Promise<Paginated<WithDbId<Skill>>> => this.service.querySkills(q);
}

export class CompetencyStore extends DataStore<Competency> {
  @observable.ref skillList?: Promise<Paginated<ListEntryWithIdentity<Skill>>>;

  public static fromService = (
    service: Service,
    identity: LoggedIn | LoggedInSuper,
    id?: DbKey<Competency>
  ): CompetencyStore => {
    /// Currently not enabled for non-Super users
    if (identity.kind !== 'super-logged-in') {
      throw new Error('Competencies cannot be managed by Client Administrator users');
    }
    const store = new CompetencyStore(new CompetencyApi(service), identity, id);
    return store;
  };

  public constructor(
    transport: TransportLayer<Competency>,
    identity: LoggedIn | LoggedInSuper,
    id?: DbKey<Competency>
  ) {
    super(transport, identity, id);
    // Don't autoload all data, we might only want a subset - hence not in base class
    this.data = this.init(id);
    if (id) {
      this.skillList = this.initSkillList(id);
    }
  }

  private readonly initSkillList = async (id: DbKey<Competency>) => {
    this.selection = this.setSelected(id);
    const skillList = await this.loadSkills();
    return skillList;
  };

  private readonly loadSkills = async (): Promise<Paginated<ListEntryWithIdentity<Skill>>> => {
    const competency = await this.selection;
    const applicationRole: AdminApplicationRoleType = this.isSuperUser ? 'super' : 'client-admin';
    if (competency) {
      const skillList = await this.querySkills(competency.id);
      return makePaginated({
        ...skillList,
        items: skillList.items.map(item => ({ id: item.id, ...item.value, identity: applicationRole }))
      });
    }
    return Promise.reject('Competency not set');
  };

  querySkills = async (id: DbKey<Competency>): Promise<Paginated<WithDbId<Skill>>> => {
    const skills = await (this.transport as CompetencyApi).querySkills(this.queryId(id, 'competencyId'));
    return skills;
  };

  @computed
  public get skillData(): MaybeLoading<Paginated<ListEntryWithIdentity<Skill>>> {
    /// Currently not enabled for non-Super users
    if (this.identity.kind === 'super-logged-in') {
      if (this.skillList) {
        return fromPromise(this.skillList);
      } else {
        return { kind: 'loading' };
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get loadingList(): MaybeLoading<Paginated<ListEntryWithIdentity<Competency>>> {
    /// Currently not enabled for non-Super users
    if (this.identity.kind === 'super-logged-in') {
      return this.loading.kind === 'value'
        ? {
          kind: this.loading.kind,
          value: makePaginated({
            ...this.loading.value,
            items: this.loading.value.items.map(item => ({ id: item.id, ...item.value, identity: 'super' }))
          })
        }
        : this.loading;
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }
}



// WEBPACK FOOTER //
// ./src/admin/stores/competency-store.ts